import React, { useLayoutEffect, useRef } from 'react';

import { Fade, List, ListItem, ListItemText, Paper } from '@material-ui/core';
import { alpha, makeStyles, useTheme } from '@material-ui/core/styles';
import ChatOutlinedIcon from '@material-ui/icons/ChatOutlined';

import { ReactComponent as MarkUnreadIcon } from 'assets/images/mark-unread-chat.svg';

interface CardContextMenuProps {
  position: { mouseX: number | null; mouseY: number | null };
  paperRef: React.RefObject<HTMLDivElement>;
  hasUnread: boolean;
  isMarkedAsUnread: boolean;
  onMarkAsRead: () => void;
  onMarkAsUnread: () => void;
}

const useStyles = makeStyles(({ spacing, palette }) => ({
  paper: {
    position: 'fixed',
    minWidth: 160,
  },
  list: {
    padding: spacing(0.5, 0),
  },
  listItem: {
    padding: spacing(0.75, 1.5),
    gap: spacing(1),
    alignItems: 'center',
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    color: alpha(palette.text.primary, 0.6),
    flexShrink: 0,
  },
}));

/**
 * Menu de contexto (botão direito) reutilizável para cards de atendimento e grupos.
 * Renderiza um Paper fixo na posição do cursor com as opções de marcar como lido/não lido.
 *
 * Exibe "Marcar como lido" quando `hasUnread` é true.
 * Exibe "Marcar como não lido" quando `isMarkedAsUnread` é false e não há contagem > 0.
 */
function CardContextMenu({
  position,
  paperRef,
  hasUnread,
  isMarkedAsUnread,
  onMarkAsRead,
  onMarkAsUnread,
}: CardContextMenuProps) {
  const classes = useStyles();
  const theme = useTheme();

  const lastPositionRef = useRef<{ mouseX: number; mouseY: number } | null>(
    null,
  );

  const isOpen = position.mouseY !== null;

  if (position.mouseX !== null && position.mouseY !== null) {
    lastPositionRef.current = {
      mouseX: position.mouseX,
      mouseY: position.mouseY,
    };
  }

  useLayoutEffect(() => {
    const lastPosition = lastPositionRef.current;
    if (isOpen && paperRef.current && lastPosition) {
      const rect = paperRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      let top = lastPosition.mouseY;
      let left = lastPosition.mouseX;

      if (top + rect.height > viewportHeight) {
        top -= rect.height;
        if (top < 0) {
          top = 8;
        }
      }

      if (left + rect.width > viewportWidth) {
        left -= rect.width;
        if (left < 0) {
          left = 8;
        }
      }

      paperRef.current.style.top = `${top}px`;
      paperRef.current.style.left = `${left}px`;
    }
  }, [isOpen, position.mouseY, position.mouseX, paperRef]);

  return (
    <Fade in={isOpen} unmountOnExit timeout={250}>
      <Paper
        ref={paperRef}
        elevation={8}
        className={classes.paper}
        style={{
          zIndex: theme.zIndex.modal,
        }}
      >
        <List component="nav" disablePadding className={classes.list}>
          {hasUnread && (
            <ListItem
              button
              onClick={onMarkAsRead}
              className={classes.listItem}
            >
              <span className={classes.icon}>
                <ChatOutlinedIcon
                  style={{ fontSize: 16 }}
                  fill={alpha(theme.palette.text.primary, 0.6)}
                />
              </span>
              <ListItemText
                primary="Marcar como lido"
                primaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          )}

          {!isMarkedAsUnread && !hasUnread && (
            <ListItem
              button
              onClick={onMarkAsUnread}
              className={classes.listItem}
            >
              <span className={classes.icon}>
                <MarkUnreadIcon
                  width={16}
                  height={16}
                  fill={alpha(theme.palette.text.primary, 0.6)}
                />
              </span>
              <ListItemText
                primary="Marcar como não lido"
                primaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          )}
        </List>
      </Paper>
    </Fade>
  );
}

export default CardContextMenu;
